import { ToastAction } from "@/components/ui/toast";

/**
 * Shared handling for premium-gated API errors.
 *
 * apiRequest/throwIfResNotOk (lib/queryClient.ts) throws an Error whose
 * `status` property carries the HTTP status code. We classify on that,
 * with a message-prefix fallback for errors built elsewhere (e.g. raw
 * fetch call sites that throw `new Error("402: ...")`).
 * - 402 → the server's requireTier gate: the user needs a higher plan.
 * - 401 → the user is not signed in.
 * - anything else → a real error worth retrying.
 */
export type GateKind = "upgrade" | "signin" | "none";

export function classifyGateError(err: unknown): GateKind {
  const e = err as { status?: unknown; message?: unknown } | null | undefined;
  const status = typeof e?.status === "number" ? e.status : undefined;
  if (status === 402) return "upgrade";
  if (status === 401) return "signin";
  if (status !== undefined) return "none";
  const msg = String(e?.message ?? err ?? "");
  if (/^402\b/.test(msg)) return "upgrade";
  if (/^401\b/.test(msg)) return "signin";
  return "none";
}

/**
 * Returns a ready-to-use toast payload for 402/401 errors, or null for
 * real errors (callers keep their existing generic error toast for those).
 */
export function premiumGateToast(err: unknown, featureName: string) {
  const kind = classifyGateError(err);
  if (kind === "upgrade") {
    return {
      title: "Upgrade to unlock",
      description: `${featureName} is part of a premium plan. Upgrade to unlock it.`,
      action: (
        <ToastAction altText="View plans" onClick={() => { window.location.href = "/pricing"; }}>
          View plans
        </ToastAction>
      ),
    };
  }
  if (kind === "signin") {
    return {
      title: "Sign in required",
      description: `Please sign in to use ${featureName}.`,
      action: (
        <ToastAction altText="Sign in" onClick={() => { window.location.href = "/login"; }}>
          Sign in
        </ToastAction>
      ),
    };
  }
  return null;
}
